<script setup lang="ts">
/**
 * ReceiveMilkOriginDialog
 *
 * Lightweight "which milk?" chooser shown before the real Receive Milk flow.
 * Presents a single question with Mother / Donor radio options; selecting
 * either one immediately emits `select` with the chosen origin and closes
 * itself. The parent is responsible for opening the corresponding
 * ReceiveMilkDialog (origin="mother" | "donor").
 */
import { DialogModal } from '@tms-phoenix/ui-vue';
import RadioButton from 'primevue/radiobutton';
import { useLocale } from '@/shared/composables/useLocale';

defineOptions({ inheritAttrs: false });

const props = withDefaults(
  defineProps<{
    visible: boolean;
    /** Which origins the current user is entitled to choose from. */
    allowMother?: boolean;
    allowDonor?: boolean;
  }>(),
  {
    allowMother: true,
    allowDonor: true,
  },
);

const emit = defineEmits<{
  'update:visible': [value: boolean];
  select: [origin: 'mother' | 'donor'];
}>();

const { t } = useLocale();

const selectedOrigin = ref<'mother' | 'donor' | null>(null);

watch(
  () => props.visible,
  (visible) => {
    if (visible) selectedOrigin.value = null;
  },
);

function choose(origin: 'mother' | 'donor') {
  selectedOrigin.value = origin;
  emit('select', origin);
  emit('update:visible', false);
}
</script>

<template>
  <DialogModal
    :visible="visible"
    :title="t('inventory.receiveMilk')"
    max-width="max-w-md"
    position="center"
    :show-footer="false"
    @update:visible="emit('update:visible', $event)"
  >
    <div class="flex flex-col gap-3">
      <label class="block text-sm font-medium text-gray-900">{{ t('inventory.receiveMilkOriginQuestion') }}</label>
      <div class="flex items-center gap-6">
        <div
          v-if="props.allowMother"
          class="flex items-center gap-2"
        >
          <RadioButton
            :model-value="selectedOrigin"
            input-id="receiveMilkOriginMother"
            name="receiveMilkOrigin"
            value="mother"
            @update:model-value="choose('mother')"
          />
          <label
            for="receiveMilkOriginMother"
            class="text-sm cursor-pointer"
          >{{ t('common.mother') }}</label>
        </div>
        <div
          v-if="props.allowDonor"
          class="flex items-center gap-2"
        >
          <RadioButton
            :model-value="selectedOrigin"
            input-id="receiveMilkOriginDonor"
            name="receiveMilkOrigin"
            value="donor"
            @update:model-value="choose('donor')"
          />
          <label
            for="receiveMilkOriginDonor"
            class="text-sm cursor-pointer"
          >{{ t('common.donor') }}</label>
        </div>
      </div>
    </div>
  </DialogModal>
</template>
